<template>
  <!-- 产品添加/编辑模态框 -->
  <div v-if="show" class="fixed inset-0 z-50 overflow-y-auto">
    <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
      <!-- 背景遮罩 -->
      <div 
        v-if="show"
        class="fixed inset-0 transition-opacity"
        @click="handleCancel"
      >
        <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
      </div>

      <!-- 模态框内容 -->
      <div class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6 w-[90vw] max-w-[90vw] mx-auto">
        <!-- 标题 -->
        <div class="flex items-center justify-between mb-6">
          <div>
          <h3 class="text-lg font-medium text-gray-900">
            {{ isEdit ? '编辑产品' : '添加产品' }}
          </h3>
            <p class="text-sm text-gray-500 mt-1 hidden sm:block">
              {{ isEdit ? '修改产品基本信息和自定义属性' : '创建新产品并设置相关属性' }}
            </p>
          </div>
          <button
            @click="handleCancel"
            class="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
          >
            <XMarkIcon class="h-6 w-6" />
          </button>
        </div>

        <!-- Tab 导航 -->
        <div v-if="hasCustomFields" class="border-b border-gray-200 mb-6">
          <nav class="-mb-px flex space-x-4 sm:space-x-8">
            <button
              v-for="tab in availableTabs"
              :key="tab.id"
              @click="activeTab = tab.id"
              :class="[
                'py-2 px-2 sm:px-1 border-b-2 font-medium text-sm whitespace-nowrap flex-1 sm:flex-initial',
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              ]"
            >
              {{ tab.name }}
            </button>
          </nav>
        </div>

        <!-- 表单内容 -->
        <form @submit.prevent="handleSubmit" class="space-y-4">
          <!-- 基本信息 Tab -->
          <div v-show="!hasCustomFields || activeTab === 'basic'" class="space-y-4">
          <!-- 产品名称 -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              产品名称 <span class="text-red-500">*</span>
            </label>
            <input
              v-model="form.name"
              type="text"
              placeholder="请输入产品名称"
                class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
              :disabled="submitting"
              required
            >
            <p v-if="errors.name" class="mt-1 text-sm text-red-600">{{ errors.name }}</p>
          </div>

          <!-- 产品编号和品牌 -->
          <div class="grid grid-cols-1 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                产品编号 <span class="text-red-500">*</span>
              </label>
              <input
                v-model="form.code"
                type="text"
                placeholder="请输入产品编号"
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                :disabled="submitting"
                required
              >
              <p v-if="errors.code" class="mt-1 text-sm text-red-600">{{ errors.code }}</p>
            </div>

          </div>

          <!-- 品牌和分组 -->
          <div class="grid grid-cols-2 gap-2 sm:gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">品牌</label>
              <input
                v-model="form.brand"
                type="text"
                placeholder="请输入品牌名称"
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                :disabled="submitting"
              >
              <p v-if="errors.brand" class="mt-1 text-sm text-red-600">{{ errors.brand }}</p>
            </div>

            <!-- 产品分组 -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">产品分组</label>
              <SearchableSelect
                v-model="form.group"
                :options="groupOptions"
                placeholder="选择分组"
                :disabled="submitting"
              />
              <p v-if="errors.group" class="mt-1 text-sm text-red-600">{{ errors.group }}</p>
            </div>
          </div>

          <!-- 产品描述 -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">产品描述</label>
            <textarea
              v-model="form.description"
              rows="3"
              placeholder="请输入产品描述"
                class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none resize-none"
              :disabled="submitting"
            ></textarea>
            <p v-if="errors.description" class="mt-1 text-sm text-red-600">{{ errors.description }}</p>
          </div>

          <!-- 产品图片 -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">产品图片</label>
            
            <!-- 当有图片时的预览区域 -->
              <div v-if="imagePreview">
                <!-- 图片预览 -->
                <div class="relative group bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border-2 border-gray-200 hover:border-blue-300 overflow-hidden shadow-sm transition-all duration-200">
                <img 
                  :src="imagePreview" 
                  alt="产品图片预览"
                    class="w-full h-40 object-cover"
                >
                  
                  <!-- 悬浮操作按钮 -->
                  <div class="absolute top-3 right-3 flex space-x-2 opacity-0 group-hover:opacity-100 transition-all duration-200">
                <button
                  type="button"
                  @click="triggerFileInput"
                      class="p-2 text-white bg-blue-600/80 hover:bg-blue-700 rounded-full backdrop-blur-sm border border-white/20 shadow-lg transition-all duration-200 flex items-center justify-center"
                  :disabled="submitting"
                      title="更换图片"
                >
                      <PhotoIcon class="h-4 w-4" />
                </button>
                <button
                  type="button"
                  @click="removeImage"
                      class="p-2 text-white bg-red-600/80 hover:bg-red-700 rounded-full backdrop-blur-sm border border-white/20 shadow-lg transition-all duration-200 flex items-center justify-center"
                  :disabled="submitting"
                      title="删除图片"
                >
                      <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                </button>
                  </div>

                  <!-- 悬停遮罩 -->
                  <div class="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-200 pointer-events-none">
                  </div>
              </div>
            </div>

            <!-- 没有图片时的上传区域 -->
            <div v-else>
              <div 
                  class="group relative border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-blue-400 hover:bg-gradient-to-br hover:from-blue-50 hover:to-blue-100 transition-all duration-300 cursor-pointer bg-gradient-to-br from-gray-50 to-gray-100"
                @click="triggerFileInput"
                @dragover.prevent
                @drop.prevent="handleDrop"
              >
                  <!-- 背景装饰 -->
                  <div class="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-100/0 via-blue-100/20 to-blue-100/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  
                  <!-- 内容 -->
                  <div class="relative z-10">
                    <div class="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full mb-3 group-hover:from-blue-200 group-hover:to-blue-300 transition-all duration-300">
                      <PhotoIcon class="h-6 w-6 text-blue-600 group-hover:text-blue-700 transition-colors duration-300" />
                    </div>
                    <p class="text-sm font-medium text-gray-700 mb-1 group-hover:text-blue-700 transition-colors duration-300">点击上传或拖拽图片到此处</p>
                    <p class="text-xs text-gray-500 group-hover:text-blue-600 transition-colors duration-300">支持 PNG、JPG、JPEG，最大 5MB</p>
                  </div>
              </div>
            </div>
            
            <input
              ref="fileInput"
              type="file"
              accept="image/*"
              @change="handleImageSelect"
              class="hidden"
              :disabled="submitting"
            >
            <p v-if="errors.image" class="mt-1 text-sm text-red-600">{{ errors.image }}</p>
          </div>

          </div>

          <!-- 自定义字段 Tab -->
          <div v-show="hasCustomFields && activeTab === 'custom'" class="space-y-4">
            <div class="space-y-4">
              <div v-for="field in customFields" :key="field.id">
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  {{ field.label }}
                  <span v-if="field.is_required" class="text-red-500">*</span>
                </label>

                <!-- 文本输入 -->
                <input
                  v-if="field.field_type === 'text'"
                  v-model="customFieldValues[field.name]"
                  type="text"
                  :placeholder="field.placeholder"
                  :maxlength="field.max_length"
                  :disabled="submitting"
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                >

                <!-- 多行文本 -->
                <textarea
                  v-else-if="field.field_type === 'textarea'"
                  v-model="customFieldValues[field.name]"
                  rows="3"
                  :placeholder="field.placeholder"
                  :maxlength="field.max_length"
                  :disabled="submitting"
                  class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none resize-none"
                ></textarea>

                <!-- 数字输入 -->
                <input
                  v-else-if="field.field_type === 'number'"
                  v-model.number="customFieldValues[field.name]"
                  type="number"
                  :placeholder="field.placeholder"
                  :min="field.min_value"
                  :max="field.max_value"
                  :disabled="submitting"
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                >

                <!-- 小数输入 -->
                <input
                  v-else-if="field.field_type === 'decimal'"
                  v-model.number="customFieldValues[field.name]"
                  type="number"
                  step="0.01"
                  :placeholder="field.placeholder"
                  :min="field.min_value"
                  :max="field.max_value"
                  :disabled="submitting"
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                >

                <!-- 日期输入 -->
                <input
                  v-else-if="field.field_type === 'date'"
                  v-model="customFieldValues[field.name]"
                  type="date"
                  :disabled="submitting"
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                >

                <!-- 日期时间输入 -->
                <input
                  v-else-if="field.field_type === 'datetime'"
                  v-model="customFieldValues[field.name]"
                  type="datetime-local"
                  :disabled="submitting"
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                >

                <!-- 布尔值选择 -->
                <div v-else-if="field.field_type === 'boolean'" class="flex items-center">
            <input
                    v-model="customFieldValues[field.name]"
                    :id="`field_${field.name}`"
              type="checkbox"
                    :disabled="submitting"
              class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  >
                  <label :for="`field_${field.name}`" class="ml-2 block text-sm text-gray-700">
                    {{ field.help_text || '启用' }}
                  </label>
                </div>

                <!-- 单选 -->
                <select
                  v-else-if="field.field_type === 'choice'"
                  v-model="customFieldValues[field.name]"
                  :disabled="submitting"
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                >
                  <option value="">请选择</option>
                  <option
                    v-for="choice in field.choices"
                    :key="choice.value"
                    :value="choice.value"
                  >
                    {{ choice.label }}
                  </option>
                </select>

                <!-- 多选 -->
                <div v-else-if="field.field_type === 'multiple_choice'" class="space-y-2">
                  <div v-for="choice in field.choices" :key="choice.value" class="flex items-center">
                    <input
                      :id="`field_${field.name}_${choice.value}`"
                      v-model="multiSelectValues[field.name]"
                      :value="choice.value"
                      type="checkbox"
              :disabled="submitting"
                      class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            >
                    <label :for="`field_${field.name}_${choice.value}`" class="ml-2 block text-sm text-gray-700">
                      {{ choice.label }}
            </label>
                  </div>
                </div>

                <!-- URL输入 -->
                <input
                  v-else-if="field.field_type === 'url'"
                  v-model="customFieldValues[field.name]"
                  type="url"
                  :placeholder="field.placeholder"
                  :disabled="submitting"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                >

                <!-- 邮箱输入 -->
                <input
                  v-else-if="field.field_type === 'email'"
                  v-model="customFieldValues[field.name]"
                  type="email"
                  :placeholder="field.placeholder"
                  :disabled="submitting"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                >

                <!-- 电话输入 -->
                <input
                  v-else-if="field.field_type === 'phone'"
                  v-model="customFieldValues[field.name]"
                  type="tel"
                  :placeholder="field.placeholder"
                  :disabled="submitting"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                >

                <p v-if="field.help_text && field.field_type !== 'boolean'" class="mt-1 text-xs text-gray-500">
                  {{ field.help_text }}
                </p>
                <p v-if="errors[`custom_${field.name}`]" class="mt-1 text-sm text-red-600">
                  {{ errors[`custom_${field.name}`] }}
                </p>
              </div>
            </div>
          </div>

          <!-- 表单按钮 -->
          <div class="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200 mt-6">
            <button
              type="button"
              @click="handleCancel"
              :disabled="submitting"
              class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
            >
              取消
            </button>
            <button
              type="submit"
              :disabled="submitting || !isFormValid"
              class="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              <svg v-if="submitting" class="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {{ submitting ? (isEdit ? '保存中...' : '创建中...') : (isEdit ? '保存' : '创建') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted } from 'vue'
import { XMarkIcon, PhotoIcon } from '@heroicons/vue/24/outline'
import { useToast } from 'vue-toastification'
import productService, { productGroupService, productCustomFieldService } from '@/services/productService'
import SearchableSelect from '@/components/common/SearchableSelect.vue'

const toast = useToast()

// Props
const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  product: {
    type: Object,
    default: null
  },
  customFieldsUpdateTrigger: {
    type: Number,
    default: 0
  }
})

// Emits
const emit = defineEmits(['close', 'success'])

// 响应式数据
const submitting = ref(false)
const fileInput = ref(null)
const imagePreview = ref('')
const selectedImageFile = ref(null)
const productGroups = ref([])
const customFields = ref([])
const customFieldValues = ref({})
const multiSelectValues = ref({})
const activeTab = ref('basic')

// Tab 定义
const tabs = ref([
  { id: 'basic', name: '基本信息' },
  { id: 'custom', name: '自定义字段' }
])

// 表单数据
const form = ref({
  name: '',
  code: '',
  brand: '',
  description: '',
  group: '',
  is_active: true
})

// 表单错误
const errors = ref({})

// 计算属性
const isEdit = computed(() => !!props.product?.id)

const isFormValid = computed(() => {
  return form.value.name.trim() && form.value.code.trim()
})

const hasCustomFields = computed(() => {
  return customFields.value.length > 0
})

const availableTabs = computed(() => {
  const baseTabs = [{ id: 'basic', name: '基本信息' }]
  if (hasCustomFields.value) {
    baseTabs.push({ id: 'custom', name: '自定义字段' })
  }
  return baseTabs
})

// 分组选项
const groupOptions = computed(() => {
  const options = [{ label: '无分组', value: '' }]
  productGroups.value.forEach(group => {
    options.push({ label: group.name, value: group.id })
  })
  return options
})

// 监听器
watch(() => props.show, (show) => {
  if (show) {
    resetForm()
    if (props.product) {
      loadProductData()
    }
  }
})

// 方法
const resetForm = () => {
  form.value = {
    name: '',
    code: '',
    brand: '',
    description: '',
    group: '',
    is_active: true
  }
  errors.value = {}
  imagePreview.value = ''
  selectedImageFile.value = null
  customFieldValues.value = {}
  multiSelectValues.value = {}
  activeTab.value = 'basic'
  
  // 重置自定义字段默认值
  customFields.value.forEach(field => {
    if (field.default_value) {
      if (field.field_type === 'multiple_choice') {
        multiSelectValues.value[field.name] = []
      } else {
        customFieldValues.value[field.name] = field.default_value
      }
    }
  })
  
  // 清空文件输入框
  if (fileInput.value) {
    fileInput.value.value = ''
  }
}

const loadProductData = () => {
  if (props.product) {
    form.value = {
      name: props.product.name || '',
      code: props.product.code || props.product.sku || '',
      brand: props.product.brand || '',
      description: props.product.description || '',
      group: props.product.group || (props.product.group_info ? props.product.group_info.id : ''),
      is_active: props.product.is_active !== false
    }
    
    // 加载现有图片
    if (props.product.image_url) {
      imagePreview.value = props.product.image_url
    } else if (props.product.image) {
      imagePreview.value = props.product.image
    }
    
    // 加载自定义字段值
    if (props.product.custom_fields) {
      Object.keys(props.product.custom_fields).forEach(fieldName => {
        const fieldData = props.product.custom_fields[fieldName]
        if (fieldData.field_type === 'multiple_choice') {
          multiSelectValues.value[fieldName] = Array.isArray(fieldData.value) ? fieldData.value : []
        } else {
          customFieldValues.value[fieldName] = fieldData.value
        }
      })
    }
  }
}

const handleImageSelect = (event) => {
  const file = event.target.files[0]
  if (!file) {
    return
  }
  processImageFile(file)
}

const handleDrop = (event) => {
  const files = event.dataTransfer.files
  if (files.length > 0) {
    processImageFile(files[0])
  }
}

const triggerFileInput = () => {
  if (fileInput.value) {
    fileInput.value.click()
  }
}

const processImageFile = (file) => {
  // 验证文件类型
  if (!file.type.startsWith('image/')) {
    toast.error('请选择图片文件')
    return
  }

  // 验证文件大小 (5MB)
  if (file.size > 5 * 1024 * 1024) {
    toast.error('图片大小不能超过5MB')
    return
  }
  
  selectedImageFile.value = file
  
  // 创建预览
  const reader = new FileReader()
  reader.onload = (e) => {
    imagePreview.value = e.target.result
  }
  reader.onerror = (e) => {
    console.error('FileReader error:', e)
    toast.error('图片读取失败')
  }
  reader.readAsDataURL(file)
  
  // 清除图片相关错误
  if (errors.value.image) {
    delete errors.value.image
  }
}

const removeImage = () => {
  imagePreview.value = ''
  selectedImageFile.value = null
  if (fileInput.value) {
    fileInput.value.value = ''
  }}

const validateForm = () => {
  errors.value = {}
  
  if (!form.value.name.trim()) {
    errors.value.name = '请输入产品名称'
  }
  
  if (!form.value.code.trim()) {
    errors.value.code = '请输入产品编号'
  }
  
  // 验证自定义字段
  customFields.value.forEach(field => {
    if (field.is_required) {
      if (field.field_type === 'multiple_choice') {
        const selectedValues = multiSelectValues.value[field.name] || []
        if (selectedValues.length === 0) {
          errors.value[`custom_${field.name}`] = `${field.label}为必填项`
        }
      } else {
        const value = customFieldValues.value[field.name]
        if (value === undefined || value === null || value === '') {
          errors.value[`custom_${field.name}`] = `${field.label}为必填项`
        }
      }
    }
    
    // 验证字段长度
    const value = customFieldValues.value[field.name]
    if (value && typeof value === 'string') {
      if (field.min_length && value.length < field.min_length) {
        errors.value[`custom_${field.name}`] = `${field.label}至少需要${field.min_length}个字符`
      }
      if (field.max_length && value.length > field.max_length) {
        errors.value[`custom_${field.name}`] = `${field.label}不能超过${field.max_length}个字符`
      }
    }
    
    // 验证数值范围
    if (field.field_type === 'number' || field.field_type === 'decimal') {
      const numValue = Number(value)
      if (!isNaN(numValue)) {
        if (field.min_value !== null && numValue < field.min_value) {
          errors.value[`custom_${field.name}`] = `${field.label}不能小于${field.min_value}`
        }
        if (field.max_value !== null && numValue > field.max_value) {
          errors.value[`custom_${field.name}`] = `${field.label}不能大于${field.max_value}`
        }
      }
    }
  })
  
  return Object.keys(errors.value).length === 0
}

const handleSubmit = async () => {
  if (!validateForm()) {
    toast.error('请检查表单信息')
    return
  }
  
  submitting.value = true
  
  try {
    // 构建FormData对象以支持文件上传
    const formData = new FormData()
    
    // 添加基本字段
    formData.append('name', form.value.name.trim())
    formData.append('code', form.value.code.trim())
    formData.append('brand', form.value.brand.trim())
    formData.append('description', form.value.description.trim())
    formData.append('is_active', form.value.is_active)
    
    // 添加分组
    if (form.value.group) {
      formData.append('group', form.value.group)
    }
    
    // 添加自定义字段值
    const customFieldData = {}
    customFields.value.forEach(field => {
      if (field.field_type === 'multiple_choice') {
        const selectedValues = multiSelectValues.value[field.name] || []
        // 对于编辑模式，包含所有字段（包括空数组），以便后端知道哪些字段需要清空
        // 对于新建模式，只包含有值的字段
        if (isEdit.value || selectedValues.length > 0) {
          customFieldData[field.name] = selectedValues
        }
      } else {
        const value = customFieldValues.value[field.name]
        
        // 对于编辑模式，包含所有字段（包括空值），以便后端知道哪些字段需要清空
        // 对于新建模式，只包含有值的字段
        if (isEdit.value || (value !== undefined && value !== null && value !== '')) {
          // 将 undefined/null 转换为空字符串
          customFieldData[field.name] = (value === undefined || value === null) ? '' : value
        }
      }
    })
    
    // 如果有自定义字段数据，或者是编辑模式且存在自定义字段定义，都发送 custom_field_values
    if (Object.keys(customFieldData).length > 0 || (isEdit.value && customFields.value.length > 0)) {
      formData.append('custom_field_values', JSON.stringify(customFieldData))
    }
    
    // 添加图片文件
    if (selectedImageFile.value && selectedImageFile.value instanceof File) {
      formData.append('image', selectedImageFile.value, selectedImageFile.value.name)
    }
    
    let response
    if (isEdit.value) {
      // 更新产品
      response = await productService.updateProduct(props.product.id, formData)
    } else {
      // 创建产品
      response = await productService.createProduct(formData)
    }
    
    if (response.success) {
      toast.success(response.message || `产品${isEdit.value ? '更新' : '创建'}成功`)
      
      // 先发送成功事件
      emit('success', response.data)
      
      // 延迟关闭模态框，确保父组件有时间处理事件
      setTimeout(() => {
        handleCancel()
      }, 100)
    } else {
      toast.error(`产品${isEdit.value ? '更新' : '创建'}失败`)
    }
    
  } catch (error) {
    console.error(`${isEdit.value ? '更新' : '创建'}产品失败:`, error)
    
    // 处理表单验证错误
    if (error.response?.status === 400 && error.response?.data) {
      const errorData = error.response.data
      
      // 设置字段错误
      if (typeof errorData === 'object') {
        const errorMessages = []
        Object.keys(errorData).forEach(key => {
          // 跳过debug_info等非字段错误
          if (key === 'debug_info') return
          
          if (errorData[key] && Array.isArray(errorData[key])) {
            errors.value[key] = errorData[key][0]
            errorMessages.push(errorData[key][0])
          } else if (typeof errorData[key] === 'string') {
            errors.value[key] = errorData[key]
            errorMessages.push(errorData[key])
          }
        })
        
        // 显示具体的错误信息
        if (errorMessages.length > 0) {
          toast.error(errorMessages[0]) // 显示第一个错误信息
        } else {
          toast.error('请检查表单信息')
        }
      } else {
        toast.error(errorData.message || `产品${isEdit.value ? '更新' : '创建'}失败`)
      }
    } else if (error.response?.status === 403) {
      toast.error('没有权限执行此操作')
    } else {
      const errorMsg = error.response?.data?.message || error.message || `产品${isEdit.value ? '更新' : '创建'}失败`
      toast.error(errorMsg)
    }
  } finally {
    submitting.value = false
  }
}

const handleCancel = () => {
  if (submitting.value) return
  
  resetForm()
  emit('close')
}

// 加载产品分组数据
const loadProductGroups = async () => {
  try {
    const response = await productGroupService.getProductGroups({
      is_active: true,
      page_size: 100
    })
    if (response.success) {
      productGroups.value = response.results
    }
  } catch (error) {
    console.error('加载产品分组失败:', error)
  }
}

// 加载自定义字段数据
const loadCustomFields = async () => {
  try {
    const response = await productCustomFieldService.getCustomFields({
      is_active: true,
      page_size: 100
    })
    if (response.success) {
      customFields.value = response.results
      
      // 初始化多选字段的值
      customFields.value.forEach(field => {
        if (field.field_type === 'multiple_choice') {
          if (!(field.name in multiSelectValues.value)) {
            multiSelectValues.value[field.name] = []
          }
        }
      })
    }
  } catch (error) {
    console.error('加载自定义字段失败:', error)
  }
}

// 监听自定义字段更新触发器
watch(() => props.customFieldsUpdateTrigger, () => {
  if (props.customFieldsUpdateTrigger > 0) {
    console.log('检测到自定义字段更新，重新加载自定义字段数据...')
    loadCustomFields()
  }
})

// 组件挂载时加载数据
onMounted(() => {
  loadProductGroups()
  loadCustomFields()
})
</script>

